import AWS_RESOURCES from './aws-resources.json';
// This file can be replaced during build by using the `fileReplacements` array.
// `ng build --prod` replaces `environment.ts` with `environment.prod.ts`.
// The list of file replacements can be found in `angular.json`.
import { DatePipe } from '@angular/common';

import {
  TupDocument,
  TupDocumentStatus,
  TupDocumentTypeId,
} from '@telmar-global/tup-document-storage';
import { TupLogLevel } from '@telmar-global/tup-logger-angular';

import { SurveysTableCellComponent } from '../app/components/surveys-table-cell/surveys-table-cell.component';

export const environment = {
  production: false,

  engines: {
    tupApiKey: 'Vyje8Vcl9b7G7fRnyw2m02o3CRKMc9sM3DT4u933',
  },

  analytics: {
    gainsight: {
      use: false,
      url: 'https://web-sdk.aptrinsic.com/api/aptrinsic.js',
      trackingId: 'AP-WTJDDJZWBM8M-2',
    },
    hotjar: {
      use: false,
      url: 'https://static.hotjar.com/c/hotjar-',
      trackingId: 2720801,
    },
  },

  // login
  tupApiKey: 'Vyje8Vcl9b7G7fRnyw2m02o3CRKMc9sM3DT4u933',
  tupAuth: {
    loginLocation: '/login',
    defaultLocation: '/',
    appName: 'Surveytime',
  },

  amplifyAuth: {
    region: AWS_RESOURCES.REGION,

    userPoolId: AWS_RESOURCES.COGNITO_USER_POOL_ID,
    userPoolWebClientId: AWS_RESOURCES.COGNITO_USER_POOL_CLIENT_ID,

    mandatorySignIn: true,
    authenticationFlowType: 'USER_PASSWORD_AUTH',
    cookieStorage: {
      // REQUIRED - Cookie domain (only required if cookieStorage is provided)
      domain: AWS_RESOURCES.WEBHOSTING_DOMAIN,
      expires: 365,
      secure: true,
    },
    // clientMetadata: { myCustomKey: 'myCustomValue' },
    // OPTIONAL - Manually set key value pairs that can be passed to Cognito Lambda Triggers
  },

  // in app options
  appOptions: {
    platformHomepage: 'https://dev.platform.telmar.com',
  },

  // engine endpoints
  api: {
    crosstab: {
      url: 'https://stage.engines.api.telmar.com/insights/stage/pluto',
      endPoint: {
        crosstab: '/crosstab',
        trending: '/trending',
        getChunkData: '/getchunkdata',
        getTrendingChunkData: '/gettrendingchunkdata',
      },
    },
    codebook: {
      url: 'https://stage.engines.api.telmar.com/codebook/st/stage/v2',
      endPoint: {
        search: '/search',
        codebookNavigation: '/codebooknavigation',
        getleafdata: '/getleafdata',
        getSurveys: '/getsurveys',
        getstabilitylevels: '/getstabilitylevels',
      },
    },
    survey: {
      url: 'https://stage.engines.api.telmar.com/survey-info/stage/v1',
      endPoint: {
        populationBases: '/surveyconfiguration/getpopulationbases',
        getdataitems: '/surveyconfiguration/getvalues',
        getreportunits: '/getreportunits',
        getSurveyMetadata: '/getsurveymetadata',
        getstabilitylevels: '/getstabilitylevels',
      },
    },
  },

  tupDocumentStorage: {
    baseUrl: 'https://6awq45bcp5.execute-api.us-east-1.amazonaws.com/prd',
    options: {
      // Configure navigation between apps / Modules (only required on the platform hompage)
      applications: [],
      // Document types (by id). Leave blank to load all document types
      types: [TupDocumentTypeId.SURVEYTIME_CAMPAIGN],
      // Document statuses. Leave blank to load all document statuses
      statuses: [],
      // Reload interval: 30s
      reloadPeriodInMs: 30 * 1000000,
      appearance: 'tup-material' as const,
      actionsMenuTrigger: 'stroked' as const,
      actions: [
        'click:edit:edit::true',
        'click:duplicate:file_copy::true',
        'click:delete:highlight_off',
        'click:export:file_download::true',
        'update_tags',
        'update_container',
      ],
      // Which columns to add to the table
      columns: [
        {
          columnDef: 'metadata.name.keyword',
          sortable: true,
          header: 'File name',
          columnType: 'string',
          cell: (document: TupDocument<any>) => document.metadata.name,
          tooltip: (document: TupDocument<unknown>) =>
            document.metadata.description,
          tooltipPosition: 'above',
          tooltipShowDelay: 100,
        },
        {
          columnDef: 'metadata.by.attributes.name.keyword',
          sortable: true,
          header: 'Author',
          columnType: 'string',
          cell: (document: TupDocument<any>) =>
            document.metadata.by.attributes.name,
        },
        {
          columnDef: 'metadata.created',
          sortable: true,
          header: 'Date created',
          columnType: 'pipe',
          cell: (document: TupDocument<any>) => document.metadata.created,
          pipe: { token: DatePipe, args: ['h:mm:ss dd/MM/yy'] },
        },
        {
          columnDef: 'metadata.modified',
          sortable: true,
          header: 'Date modified',
          columnType: 'pipe',
          cell: (document: TupDocument<any>) => document.metadata.modified,
          pipe: { token: DatePipe, args: ['h:mm:ss dd/MM/yy'] },
        },
        {
          columnDef: 'document.content.surveys',
          sortable: false,
          header: 'Surveys used',
          columnType: 'component',
          cell: () => '',
          component: SurveysTableCellComponent,
        },
        {
          columnDef: 'actions-stroked',
          sortable: false,
          header: '',
          columnType: 'actions',
          cell: () => '',
        },
      ],
      _source: ['metadata', 'content.surveys'],
      tags: {
        suggested: ['Client', 'Project'],
      },
    },
  },

  tupLogger: {
    logLevel: TupLogLevel.DEBUG,
  },

  tupUserPreferenceStorage: {
    appSyncClientOptions: {
      region: 'us-east-1',
      graphqlEndpoint:
        'https://566u6iatc5bctla7v72ekfujt4.appsync-api.us-east-1.amazonaws.com/graphql',
    },
  },

  tupAudienceGroups: {
    documentTypes: [TupDocumentTypeId.OWN_CODES_TARGET],
    status: [TupDocumentStatus.ACTIVE],
  },

  userPreferences: {
    lastTenSurveysUsed: 'survey-time.last-ten-used-surveys.v0',
  },

  googleApi: {
    clientId:
      '269176030796-an367pn9tec4p5sk795kabkh9j4hp2dh.apps.googleusercontent.com',
    apiKey: 'AIzaSyD0w6xvezia2W6VUBJ8U7hvheiY6-vhCAA',
    discoveryDocs: [
      'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest',
    ],
    scope: 'https://www.googleapis.com/auth/drive.file',
  },

  googleDrive: {
    folder: {
      name: 'survey-time',
    },
  },
};

/*
 * For easier debugging in development mode, you can import the following file
 * to ignore zone related error stack frames such as `zone.run`, `zoneDelegate.invokeTask`.
 *
 * This import should be commented out in production mode because it will have a negative impact
 * on performance if an error is thrown.
 */
// import 'zone.js/dist/zone-error';  // Included with Angular CLI.
